import { Popover } from "react-tiny-popover";
import { Button, Tooltip } from "antd";
import { FiBold, FiItalic } from "react-icons/fi";
import { BiStrikethrough } from "react-icons/bi";
import { GrOrderedList, GrUnorderedList } from "react-icons/gr";
import { BsQuote } from "react-icons/bs";
// import { LuBrackets } from "react-icons/lu";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import useMobileResponsive from "../../../../hooks/useMobileResponsive";

export default function TextFormatPopover({
  value,
  setValue,
  textAreaRef,
  children,
}: {
  textAreaRef: any;
  value: string;
  setValue: (value?: string) => void;
  children: (callbacks: { onSelect: () => void }) => React.ReactNode;
}) {
  const isMobile = useMobileResponsive();
  const [popoverVisible, setPopoverVisible] = useState(false);

  // Check if text is selected
  const isTextSelected = () => {
    const textarea = textAreaRef.current;
    if (!textarea) return false;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    return start !== end; // Return true if there’s a selection
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSelectionCheck = useCallback(
    debounce(() => {
      setPopoverVisible(isTextSelected());
    }, 300), // 300ms delay feels natural without being slow
    [isTextSelected]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedSelectionCheck.cancel();
    };
  }, [debouncedSelectionCheck]);

  // Handle formatting (e.g., adding bold, italic, etc.)
  const applyFormat = (format: string) => {
    const textarea = textAreaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    let newText = value;

    switch (format) {
      case "bold":
        newText =
          value.substring(0, start) +
          "*" +
          selectedText +
          "*" +
          value.substring(end);
        break;
      case "italic":
        newText =
          value.substring(0, start) +
          "_" +
          selectedText +
          "_" +
          value.substring(end);
        break;
      case "strikethrough":
        newText =
          value.substring(0, start) +
          "~" +
          selectedText +
          "~" +
          value.substring(end);
        break;
      case "code":
        newText =
          value.substring(0, start) +
          "`" +
          selectedText +
          "`" +
          value.substring(end);
        break;
      case "bullet":
        newText =
          value.substring(0, start) +
          "- " +
          selectedText +
          "\n" +
          value.substring(end);
        break;
      case "numbered":
        newText =
          value.substring(0, start) +
          "1. " +
          selectedText +
          "\n" +
          value.substring(end);
        break;
      case "quote":
        newText =
          value.substring(0, start) +
          "> " +
          selectedText +
          "\n" +
          value.substring(end);
        break;
      default:
        break;
    }

    setValue(newText);
    // Move cursor to the end of the formatted text
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start +
        (format === "bold" ? 4 : format === "strikethrough" ? 4 : 2) +
        selectedText.length;
    }, 0);
    setPopoverVisible(false); // Hide popover after applying format
  };

  // Content for the Popover (formatting toolbar)
  const popoverContent = (
    <div
      className="has-background-white has-border has-rounded-corners mb-2"
      style={{ display: "flex", padding: "2px" }}
    >
      <Tooltip title="Bold">
        <Button
          icon={<FiBold color="black" />}
          onClick={() => applyFormat("bold")}
          ghost
          type="text"
        />
      </Tooltip>
      <Tooltip title="Italic">
        <Button
          icon={<FiItalic color="black" />}
          onClick={() => applyFormat("italic")}
          ghost
          type="text"
        />
      </Tooltip>
      <Tooltip title="Strikethrough">
        <Button
          icon={<BiStrikethrough color="black" />}
          onClick={() => applyFormat("strikethrough")}
          ghost
          type="text"
        />
      </Tooltip>
      {/* <Tooltip title="Inline Code">
        <Button
          icon={<LuBrackets color="black" />}
          onClick={() => applyFormat("code")}
          ghost
          type="text"
        />
      </Tooltip> */}
      <Tooltip title="Bullet List">
        <Button
          icon={<GrUnorderedList color="black" />}
          onClick={() => applyFormat("bullet")}
          ghost
          type="text"
        />
      </Tooltip>
      <Tooltip title="Numbered List">
        <Button
          icon={<GrOrderedList color="black" />}
          onClick={() => applyFormat("numbered")}
          ghost
          type="text"
        />
      </Tooltip>
      <Tooltip title="Quote">
        <Button
          icon={<BsQuote color="black" />}
          onClick={() => applyFormat("quote")}
          ghost
          type="text"
        />
      </Tooltip>
    </div>
  );

  return (
    <>
      <Popover
        content={popoverContent}
        isOpen={popoverVisible}
        onClickOutside={() => setPopoverVisible(false)}
        positions={[isMobile ? "bottom" : "top"]}
        containerStyle={{
          zIndex: "1000",
        }}
        align="center" 
        contentLocation={
          isMobile
            ? () => {
                return {
                  top: window.innerHeight - 130,
                  left: window.innerWidth / 2 - 100,
                };
              }
            : undefined
        }
      >
        <p></p>
      </Popover>
      {children({
        onSelect: debouncedSelectionCheck,
      })}
    </>
  );
}
